import { useState, useRef, useCallback, useEffect } from 'react';
import {
  uploadImage,
  pollStatus,
  downloadResult,
  getErrorMessage,
  ApiError,
  type JobStatus,
  type UpscaleResult,
} from '../lib/api';
import { canProcess, recordUsage, refreshUsage, getUsageStats } from '../lib/quota';
import { track } from '../lib/analytics';

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];


function getImageFormatLabel(contentType: string): string {
  if (contentType.includes('png')) return 'PNG';
  if (contentType.includes('jpeg') || contentType.includes('jpg')) return 'JPG';
  if (contentType.includes('webp')) return 'WebP';
  return contentType || 'Image';
}

function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function formatDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return '—';
  if (ms < 1000) return `${ms} ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

export function UpscalerApp({ uploadLabel, showScale = true }: { uploadLabel: string; showScale?: boolean }) {
  const [file, setFile] = useState<File | null>(null);
  const [scale, setScale] = useState<number>(2);
  const [status, setStatus] = useState<JobStatus>('idle');
  const [progress, setProgress] = useState<number>(0);
  const [result, setResult] = useState<UpscaleResult | null>(null);
  const [uploadId, setUploadId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [usage, setUsage] = useState(() => getUsageStats());
  const [showProPrompt, setShowProPrompt] = useState(false);
  const [comparePosition, setComparePosition] = useState(50);
  const inputRef = useRef<HTMLInputElement>(null);
  const objectUrlRef = useRef<string | null>(null);

  const updateUsage = useCallback(async () => {
    try {
      const fresh = await refreshUsage();
      setUsage({ used: fresh.dailyFreeUsed, limit: fresh.dailyFreeLimit });
    } catch {
      setUsage(getUsageStats());
    }
  }, []);

  useEffect(() => {
    updateUsage();
  }, [updateUsage]);

  const validateFile = (f: File): string | null => {
    if (!ALLOWED_TYPES.includes(f.type)) return 'Please upload a JPG, PNG, or WebP image.';
    if (f.size > MAX_FILE_SIZE) return 'File size must be 5MB or smaller.';
    return null;
  };

  const revokePreview = () => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
  };

  const handleFile = (f: File) => {
    const err = validateFile(f);
    if (err) {
      setError(err);
      setErrorCode('invalid_format');
      return;
    }
    if (!canProcess()) {
      setShowProPrompt(true);
      return;
    }
    revokePreview();
    const previewUrl = URL.createObjectURL(f);
    objectUrlRef.current = previewUrl;
    setFile(f);
    setError(null);
    setErrorCode(null);
    setResult(null);
    setStatus('idle');
    setShowProPrompt(false);
    track('upload', { fileType: f.type, fileSize: f.size, source: 'dropzone' });
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f) handleFile(f);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) handleFile(f);
  };

  const setApiError = (err: unknown) => {
    if (err instanceof ApiError) {
      const code = err.code;
      setErrorCode(code);
      setError(getErrorMessage(code) || err.message);
      if (code === 'daily_quota_exceeded') setShowProPrompt(true);
      return;
    }
    setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    setErrorCode('unknown_error');
  };

  const startProcessing = async () => {
    if (!file) return;
    if (!canProcess()) {
      setShowProPrompt(true);
      return;
    }

    setError(null);
    setErrorCode(null);
    setStatus('uploading');
    setProgress(10);
    track('start_upscale', { scale, fileType: file.type, fileSize: file.size });

    try {
      const startedAt = Date.now();
      const { uploadId: id } = await uploadImage(file, scale);
      setUploadId(id);
      setStatus('queued');
      setProgress(30);

      const statusRes = await pollStatus(id, (s, p) => {
        setStatus(s);
        setProgress(p);
      });

      if (statusRes.status === 'error') {
        const message = typeof statusRes.error === 'object'
          ? statusRes.error.message
          : statusRes.error;
        throw new ApiError(
          message || 'Processing failed.',
          statusRes.errorCode || 'processing_failed',
          0
        );
      }

      const originalUrl = objectUrlRef.current || URL.createObjectURL(file);
      if (!objectUrlRef.current) objectUrlRef.current = originalUrl;

      const resultUrl = statusRes.resultUrl;
      if (!resultUrl) {
        throw new ApiError('Result URL is missing.', 'not_found', 404);
      }

      // Download result through the authenticated endpoint so the preview works.
      const resultBlob = await downloadResult(id);
      const resultObjectUrl = URL.createObjectURL(resultBlob);

      const outputFormat = getImageFormatLabel(resultBlob.type);
      const outputSize = resultBlob.size;
      const processingMs = Date.now() - startedAt;

      const originalImg = new Image();
      const img = new Image();
      await Promise.all([
        new Promise<void>((resolve, reject) => {
          originalImg.onload = () => resolve();
          originalImg.onerror = () => reject(new Error('Failed to load original preview'));
          originalImg.src = originalUrl;
        }),
        new Promise<void>((resolve, reject) => {
          img.onload = () => resolve();
          img.onerror = () => reject(new Error('Failed to load upscaled preview'));
          img.src = resultObjectUrl;
        }),
      ]);

      recordUsage();
      updateUsage();
      setResult({
        originalUrl,
        upscaledUrl: resultObjectUrl,
        scale,
        width: img.naturalWidth,
        height: img.naturalHeight,
        originalWidth: originalImg.naturalWidth,
        originalHeight: originalImg.naturalHeight,
        outputSize,
        outputFormat,
        processingMs,
      });
      setStatus('done');
      setProgress(100);
      track('success', { scale, width: img.naturalWidth, height: img.naturalHeight });
    } catch (err) {
      setStatus('error');
      const message = err instanceof Error ? err.message : 'Something went wrong. Please try again.';
      setError(message);
      setErrorCode(err instanceof ApiError ? err.code : 'unknown_error');
      track('fail', { scale, fileType: file.type, reason: message });
    }
  };

  const handleDownload = async () => {
    if (!result || !uploadId) return;
    try {
      const blob = await downloadResult(uploadId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ext = blob.type === 'image/png'
        ? 'png'
        : blob.type === 'image/jpeg'
          ? 'jpg'
          : blob.type === 'image/webp'
            ? 'webp'
            : file?.name.split('.').pop() || 'png';
      a.download = `upscaled-${Date.now()}.${ext}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      track('download', { scale: result.scale, width: result.width, height: result.height });
    } catch (err) {
      setApiError(err);
    }
  };

  const isProcessing = status === 'uploading' || status === 'queued' || status === 'processing';

  return (
    <div className="mx-auto w-full max-w-3xl rounded-2xl border border-border-subtle bg-bg-elevated p-4 shadow-sm sm:p-6">
      {!file && (
        <div
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
          onClick={() => inputRef.current?.click()}
          className="flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-border-strong bg-bg-elevated-2 px-6 py-12 text-center transition-colors hover:border-primary hover:bg-primary-50"
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && inputRef.current?.click()}
        >
          <div className="mb-4 rounded-full bg-primary-50 p-3 text-primary">
            <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
          <p className="text-base font-semibold text-text-primary">{uploadLabel}</p>
          <p className="mt-1 text-sm text-text-secondary">Drag & drop or click to select</p>
          <p className="mt-2 text-xs text-text-muted">JPG, PNG, WebP · up to 5MB</p>
          <input
            ref={inputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={handleInputChange}
          />
        </div>
      )}

      {file && !result && (
        <div className="space-y-4">
          <div className="relative overflow-hidden rounded-lg border border-border-subtle bg-bg-elevated-2">
            <img
              src={objectUrlRef.current || URL.createObjectURL(file)}
              alt="Preview"
              className="max-h-80 w-full object-contain"
            />
            <button
              onClick={() => {
                revokePreview();
                setFile(null);
                setStatus('idle');
              }}
              className="absolute right-2 top-2 rounded-full bg-bg-elevated/90 p-1 text-text-secondary shadow hover:text-text-primary"
              aria-label="Remove file"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {showScale && (
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-sm font-medium text-text-secondary">Upscale:</span>
              <button
                onClick={() => setScale(2)}
                className={`rounded-full px-4 py-2 text-sm font-semibold ${
                  scale === 2
                    ? 'bg-primary text-white'
                    : 'bg-bg-elevated-2 text-text-secondary hover:bg-bg-elevated-3'
                }`}
              >
                2× Free
              </button>
              <button
                onClick={() => {
                  setScale(2);
                  setShowProPrompt(true);
                }}
                className="inline-flex items-center gap-1.5 rounded-full bg-bg-elevated-2 px-3 py-2 text-sm font-semibold text-text-secondary opacity-60 cursor-not-allowed"
                disabled
                title="4× Pro is planned. Free preview uses 2×."
              >
                4× Pro
                <span className="rounded-full bg-warning/20 px-2 py-0.5 text-xs font-medium text-warning">
                  Coming soon
                </span>
              </button>
            </div>
          )}

          {showProPrompt && (
            <div className="rounded-lg bg-warning/10 p-3 text-sm text-warning">
              4× Pro is planned. Free preview uses 2×.
            </div>
          )}

          <button
            onClick={startProcessing}
            disabled={isProcessing}
            className="w-full rounded-xl bg-primary py-3 text-base font-semibold text-white hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isProcessing ? 'Processing…' : 'Upscale Image'}
          </button>

          {isProcessing && (
            <div className="space-y-2">
              <div className="h-2 w-full overflow-hidden rounded-full bg-bg-elevated-2">
                <div
                  className="h-full bg-primary transition-all"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <p className="text-center text-sm text-text-secondary">
                {status === 'uploading' && 'Uploading…'}
                {status === 'queued' && 'In queue…'}
                {status === 'processing' && 'AI is enlarging your image…'}
              </p>
            </div>
          )}
        </div>
      )}

      {result && (
        <div className="space-y-5">
          <div className="rounded-xl border border-border-subtle bg-bg-elevated-2 p-3">
            <div className="mb-3 flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-text-primary">Before / After comparison</p>
                <p className="text-xs text-text-secondary">Drag the slider to compare real pixels.</p>
              </div>
              <span className="rounded-full bg-primary-50 px-3 py-1 text-xs font-semibold text-primary">{comparePosition}% after</span>
            </div>
            <div className="relative aspect-video overflow-hidden rounded-lg bg-bg-elevated">
              <img src={result.originalUrl} alt="Original" className="absolute inset-0 h-full w-full object-contain" />
              <div className="absolute inset-0 overflow-hidden" style={{ clipPath: `inset(0 ${100 - comparePosition}% 0 0)` }}>
                <img src={result.upscaledUrl} alt="Upscaled" className="h-full w-full object-contain" />
              </div>
              <div className="absolute inset-y-0 w-0.5 bg-white shadow" style={{ left: `${comparePosition}%` }} />
              <span className="absolute left-3 top-3 rounded bg-black/60 px-2 py-1 text-xs font-medium text-white">Original</span>
              <span className="absolute right-3 top-3 rounded bg-primary/80 px-2 py-1 text-xs font-medium text-white">Upscaled</span>
            </div>
            <input
              type="range"
              min="0"
              max="100"
              value={comparePosition}
              onChange={(e) => setComparePosition(Number(e.target.value))}
              className="mt-3 w-full accent-primary"
              aria-label="Before after comparison slider"
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="rounded-lg border border-border-subtle p-3">
              <p className="mb-2 text-xs font-medium text-text-secondary">Original 100% crop</p>
              <div className="mx-auto h-40 w-40 overflow-hidden rounded bg-bg-elevated-2">
                <img src={result.originalUrl} alt="Original detail crop" className="max-w-none" style={{ width: result.originalWidth, height: result.originalHeight, transform: 'translate(-20px, -20px)' }} />
              </div>
              <p className="mt-2 text-center text-xs text-text-muted">{result.originalWidth} × {result.originalHeight}px</p>
            </div>
            <div className="rounded-lg border border-border-subtle p-3">
              <p className="mb-2 text-xs font-medium text-text-secondary">Upscaled 100% crop</p>
              <div className="mx-auto h-40 w-40 overflow-hidden rounded bg-bg-elevated-2">
                <img src={result.upscaledUrl} alt="Upscaled detail crop" className="max-w-none" style={{ width: result.width, height: result.height, transform: 'translate(-80px, -80px)' }} />
              </div>
              <p className="mt-2 text-center text-xs text-text-muted">{result.width} × {result.height}px</p>
            </div>
          </div>

          <div className="grid gap-3 rounded-xl border border-border-subtle bg-bg-elevated-2 p-4 text-sm text-text-secondary sm:grid-cols-4">
            <div><span className="block text-xs text-text-muted">Output</span><strong className="text-text-primary">{result.width} × {result.height}px</strong></div>
            <div><span className="block text-xs text-text-muted">Format</span><strong className="text-text-primary">{result.outputFormat}</strong></div>
            <div><span className="block text-xs text-text-muted">File size</span><strong className="text-text-primary">{formatBytes(result.outputSize)}</strong></div>
            <div><span className="block text-xs text-text-muted">AI time</span><strong className="text-text-primary">{formatDuration(result.processingMs)}</strong></div>
          </div>

          <button
            onClick={handleDownload}
            className="w-full rounded-xl bg-primary py-3 text-base font-semibold text-white hover:bg-primary-hover"
          >
            Download Image ({result.outputFormat}, {formatBytes(result.outputSize)})
          </button>
          <button
            onClick={() => {
              revokePreview();
              setFile(null);
              setResult(null);
              setStatus('idle');
            }}
            className="w-full rounded-xl border border-border-subtle bg-bg-elevated-2 py-3 text-base font-semibold text-text-secondary hover:bg-bg-elevated-3"
          >
            Upload Another
          </button>
        </div>
      )}

      {error && (
        <div className="mt-4 rounded-lg bg-error/10 p-3 text-sm text-error">
          {error}
          {errorCode && import.meta.env.DEV && (
            <span className="ml-2 opacity-70">({errorCode})</span>
          )}
        </div>
      )}

      {showProPrompt && (
        <div className="mt-4 rounded-xl border border-primary-100 bg-primary-50 p-4 text-center">
          <h3 className="font-semibold text-text-primary">Need higher resolution?</h3>
          <p className="mt-1 text-sm text-text-secondary">
            Upgrade to Pro for 4× upscales and high-resolution downloads.
          </p>
          <a
            href="/pricing"
            onClick={() => track('pricing_click', { location: 'upscaler_pro_prompt', plan: 'pro' })}
            className="mt-3 inline-block rounded-full bg-primary px-5 py-2 text-sm font-semibold text-white hover:bg-primary-hover"
          >
            Upgrade to Pro
          </a>
        </div>
      )}

      <div className="mt-4 text-center text-xs text-text-muted">
        Free quota: {usage.used} / {usage.limit} today
      </div>
    </div>
  );
}
